<script setup lang="ts">
import { useElementalForm } from '../ElementalFormContextProvider/context'
import { useContainerSize } from '@/composables/useContainerSize'
import { formatThousands } from '@/utils/formatting'

const props = defineProps<Props>()

defineOptions({
  name: 'FormSection',
})

interface Props {
  label?: string
  colapsable?: boolean
  isOpen?: boolean
  sectionId: string
  fullWidth?: boolean
  description?: string
  counter?: number
  class?: string
  noContent?: boolean
}

const [isOpen, toggleOpen] = useToggle(props.isOpen || false)
const { isSidebarView, formId } = useElementalForm()
const { isSmall } = useContainerSize()

useCustomEventListener('scroll-to', ({ id }) => {
  if (props.sectionId !== id)
    return
  if (props.sectionId && isSidebarView?.value) {
    const form = document.getElementById(formId.value)
    if (!form)
      return
    const element = form?.querySelector(`#${props.sectionId}`)
    if (element)
      element.scrollIntoView({ behavior: 'smooth' })
  }
})
</script>

<template>
  <div
    :id="sectionId"
    class="mx-auto bg-white md:rounded-xl  w-full flex flex-col gap-2"
  >
    <div
      v-if="label"
      class="flex items-center justify-between w-full cursor-pointer mx-a"
      :class="[
        !isSmall && 'p-4 pb-3',
        isSmall && 'p-2 md:p-4',
        !isOpen && isSmall && '!pb-2',
        !fullWidth && 'max-w-5xl',
      ]"
      @click="toggleOpen()"
    >
      <div class="flex whitespace-nowrap items-center gap-1.5">
        <HeadingV2 level="3">
          {{ label }}
        </HeadingV2>
        <BubbleCount v-if="counter !== undefined">
          {{ formatThousands(counter, false) }}
        </BubbleCount>
      </div>

      <div class="flex items-center gap-4">
        <TextV2 v-if="description && !isSmall" class="body-14 color-dark-gray">
          {{ description }}
        </TextV2>

        <div
          v-if="colapsable"
          i-material-symbols:keyboard-arrow-down
          transition="transform 200ms ease-in-out"
          :class="{
            'rotate-180': isOpen,
          }"
        />
      </div>
    </div>
    <Collapsable v-if="colapsable" :aria-expanded="isOpen">
      <div
        class="flex flex-col items-stretch gap-7 w-full"
        :class="$style['no-scrollbar']"
      >
        <slot name="full-width" />

        <div
          class="flex flex-col items-stretch gap-7 w-full mx-auto"
          :class="[
            {
              'p-4 pb-3': !isSmall,
              'p-2 md:p-4': isSmall,
              '!pb-2': !isOpen && isSmall,
              'max-w-5xl': !fullWidth,
            },
          ]"
        >
          <slot />
        </div>
      </div>
    </Collapsable>
    <div
      v-else
      :class="$style['no-scrollbar']"

      class="flex flex-col items-stretch gap-7 w-full"
    >
      <slot name="full-width" />
      <div
        v-if="!noContent"
        class="flex flex-col items-stretch gap-7 w-full mx-auto"
        :class="[
          $style['no-scrollbar'],
          {
            'p-4 pb-3': !isSmall,
            'p-2 md:p-4': isSmall,
            '!pb-2': !isOpen && isSmall,
            'max-w-5xl': !fullWidth,
          },
        ]"
      >
        <slot />
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
.no-scrollbar {
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
